import { Children, cloneElement, isValidElement, type ReactNode } from 'react';

/**
 * Put a space either side of every `<br>` in a node tree.
 *
 * Hero headings are written as `{lineA}<br />{lineB}`, which renders correctly
 * and reads correctly in a browser: Chrome's `innerText` turns a `<br>` into a
 * newline, so Google — which renders with Chrome — sees two words.
 *
 * Nothing else does. `textContent`, every naive tag-stripping extractor, and
 * most of the AI crawlers that fetch raw HTML rather than rendering it, all
 * drop the tag and concatenate what was either side of it:
 *
 *   <h1>AI customer support<br/>for your industry</h1>
 *     innerText     "AI customer support\nfor your industry"
 *     textContent   "AI customer supportfor your industry"
 *
 * So the H1 an answer engine reads is a word that does not exist, on the
 * element those models weight most heavily. Both of the site's hero components
 * pass their title and subtitle through here, which fixes every page built on
 * them at once — the alternative was a space typed by hand at 160 call sites,
 * where the next hero written would reintroduce it anyway.
 *
 * The added space is invisible: it sits at the end of a line that is being
 * broken, where it collapses.
 */
export function spaceBreaks(node: ReactNode): ReactNode {
  return Children.map(node, (child) => {
    if (!isValidElement(child)) return child;
    if (child.type === 'br') return <> {child} </>;
    const { children } = child.props as { children?: ReactNode };
    return children ? cloneElement(child, {}, spaceBreaks(children)) : child;
  });
}
